// src/components/professor/Support.js
import React, { useState, useEffect, useRef } from 'react';
import { auth, db, storage } from '../../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import Preloader from '../../components/Preloader';
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  getDoc,
  deleteDoc,
  getDocs,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { FaPaperclip } from 'react-icons/fa'; // Import the attachment icon

// Import the createTicket function
import { createTicket } from '../../api/ticketApi';

function Support() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'Low',
    attachments: [],
  });
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentTicket, setCurrentTicket] = useState(null); // Store the ticket data
  const [messages, setMessages] = useState([]); // Store messages
  const [newMessage, setNewMessage] = useState(''); // New message input
  const [view, setView] = useState('list'); // 'list', 'chat', 'form'
  const [messagesUnsubscribe, setMessagesUnsubscribe] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null); // For image attachment in chat
  const [modalImageUrl, setModalImageUrl] = useState(null); // For displaying full-size images

  // Refs for file inputs
  const attachmentInputRef = useRef(null);
  const chatAttachmentInputRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setFormData((prev) => ({
          ...prev,
          email: currentUser.email,
        }));
      } else {
        window.location.href = '/sign-in';
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Fetch user's tickets
  useEffect(() => {
    if (user) {
      const ticketsCollectionRef = collection(db, 'tickets');
      const q = query(
        ticketsCollectionRef,
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const userTickets = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setTickets(userTickets);
      });

      return () => unsubscribe();
    }
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      window.location.href = '/sign-in';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'attachments') {
      setFormData((prev) => ({
        ...prev,
        attachments: files ? Array.from(files) : [],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleAttachmentClick = () => {
    attachmentInputRef.current.click();
  };

  const handleChatAttachmentClick = () => {
    chatAttachmentInputRef.current.click();
  };

  const handleChatAttachmentChange = (e) => {
    setSelectedImage(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmissionStatus(null);
    setSubmitting(true);

    try {
      // Call the createTicket function with formData
      const ticketNumber = await createTicket({
        ...formData,
      });

      setSubmissionStatus(
        `Ticket submitted successfully! Your ticket number is ${ticketNumber}.`
      );

      // Reset the form data
      setFormData({
        name: '',
        email: user.email, // keep the logged-in user’s email
        subject: '',
        message: '',
        priority: 'Low',
        attachments: [],
      });

      // Fetch the new ticket data from Firestore
      const ticketDocRef = doc(db, 'tickets', ticketNumber);
      const ticketSnapshot = await getDoc(ticketDocRef);
      if (ticketSnapshot.exists()) {
        const newTicket = { id: ticketNumber, ...ticketSnapshot.data() };
        setCurrentTicket(newTicket);
        openTicket(newTicket);
      } else {
        console.error('Ticket not found after creation.');
      }
    } catch (error) {
      console.error('Error submitting ticket:', error);
      setSubmissionStatus(error.message || 'Error submitting the ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openTicket = (ticket) => {
    setCurrentTicket(ticket);
    setView('chat');

    // Set up real-time listener for messages
    const ticketDocRef = doc(db, 'tickets', ticket.id);
    const messagesCollectionRef = collection(ticketDocRef, 'messages');
    const q = query(messagesCollectionRef, orderBy('timestamp', 'asc'));

    // Clean up previous listener
    if (messagesUnsubscribe) {
      messagesUnsubscribe();
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setMessages(msgs);
    });

    setMessagesUnsubscribe(() => unsubscribe);
  };

  const sendMessage = async () => {
    if (newMessage.trim() === '' && !selectedImage) return;
    if (!currentTicket) return;

    const messageData = {
      sender: 'user',
      messageText: newMessage,
      timestamp: serverTimestamp(),
      userId: user.uid, // Include userId for security rules
    };

    try {
      if (selectedImage) {
        // Upload the image to Firebase Storage
        const imageRef = ref(
          storage,
          `tickets/${currentTicket.id}/messages/${Date.now()}_${selectedImage.name}`
        );
        const snapshot = await uploadBytes(imageRef, selectedImage);
        const downloadURL = await getDownloadURL(snapshot.ref);

        messageData.imageUrl = downloadURL;
        messageData.imageName = selectedImage.name;
      }

      const ticketDocRef = doc(db, 'tickets', currentTicket.id);
      const messagesCollectionRef = collection(ticketDocRef, 'messages');
      await addDoc(messagesCollectionRef, messageData);

      setNewMessage(''); // Clear the input field
      setSelectedImage(null); // Clear the selected image
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const deleteTicket = async (ticketId) => {
    if (
      window.confirm(
        'Are you sure you want to delete this ticket? This action cannot be undone.'
      )
    ) {
      try {
        const ticketDocRef = doc(db, 'tickets', ticketId);

        // Check if the current user is the owner of the ticket
        const ticketDoc = await getDoc(ticketDocRef);
        if (ticketDoc.exists() && ticketDoc.data().userId === user.uid) {
          // Delete attachments associated with the ticket
          const ticketData = ticketDoc.data();

          if (ticketData.attachment && ticketData.attachment.length > 0) {
            const deleteAttachmentPromises = ticketData.attachment.map(async (url) => {
              try {
                const fileRef = ref(storage, url);
                await deleteObject(fileRef);
              } catch (error) {
                console.error('Error deleting attachment from storage:', error);
              }
            });
            await Promise.all(deleteAttachmentPromises);
          }

          // Delete all messages in the subcollection and their attachments
          const messagesCollectionRef = collection(ticketDocRef, 'messages');
          const messagesSnapshot = await getDocs(messagesCollectionRef);
          const deleteMessagePromises = messagesSnapshot.docs.map(async (messageDoc) => {
            const messageData = messageDoc.data();

            // Delete any attachments in messages
            if (messageData.imageUrl) {
              try {
                const imageRef = ref(storage, messageData.imageUrl);
                await deleteObject(imageRef);
              } catch (error) {
                console.error('Error deleting message attachment from storage:', error);
              }
            }

            // Delete the message document
            await deleteDoc(messageDoc.ref);
          });
          await Promise.all(deleteMessagePromises);

          // Delete the ticket document
          await deleteDoc(ticketDocRef);

          // Update the tickets list
          setTickets(tickets.filter((ticket) => ticket.id !== ticketId));

          // If the deleted ticket was open, close the view
          if (currentTicket && currentTicket.id === ticketId) {
            setCurrentTicket(null);
            setView('list');
          }

          // Optionally, display a success message
        } else {
          alert('You are not authorized to delete this ticket.');
        }
      } catch (error) {
        console.error('Error deleting ticket:', error);
        alert('An error occurred while deleting the ticket. Please try again.');
      }
    }
  };


  const openImageModal = (imageUrl) => {
    setModalImageUrl(imageUrl);
  };

  const closeImageModal = () => {
    setModalImageUrl(null);
  };

  const goBack = () => {
    if (messagesUnsubscribe) {
      messagesUnsubscribe();
      setMessagesUnsubscribe(null);
    }
    setCurrentTicket(null);
    setView('list');
  };

  if (loading) {
    return <Preloader />;
  }

  return (

    <section className="py-8 px-4 bg-[var(--section-bg)] text-[var(--text-color)] min-h-screen">
      <div className="max-w-4xl mx-auto mt-4">
        {view === 'list' && (
          <>
            <h3 className="text-xl font-semibold mb-4">Your Tickets</h3>
            <button
              className="px-4 py-2 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded mb-3 transition-colors"
              onClick={() => setView('form')}
            >
              Submit New Ticket
            </button>
            {tickets.length > 0 ? (
              <ul className="space-y-4">
                {tickets.map((ticket) => (
                  <li
                    key={ticket.id}
                    className="flex justify-between items-center p-4 rounded shadow bg-[var(--card-bg)] hover:bg-[var(--card-hover-bg)] transition-colors"
                  >
                    <div
                      onClick={() => openTicket(ticket)}
                      className="cursor-pointer flex-grow"
                    >
                      <strong>{ticket.subject}</strong>
                      <br />
                      <span>
                        Status: {ticket.status} - Priority: {ticket.priority}
                      </span>
                    </div>
                    <button
                      className="px-3 py-1 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded ml-2 transition-colors"
                      onClick={() => deleteTicket(ticket.id)}
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[var(--muted-color)]">You have no tickets.</p>
            )}
          </>
        )}
  
        {view === 'form' && (
          <div className="max-w-md mx-auto bg-[var(--card-bg)] p-6 rounded shadow">
            <h3 className="text-center text-xl font-semibold mb-4">
              Submit a Support Ticket
            </h3>
            {submissionStatus && (
              <div
                className={`p-3 mb-4 rounded ${
                  submissionStatus.startsWith('Ticket submitted')
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
                role="alert"
              >
                {submissionStatus}
              </div>
            )}
            <form className="space-y-4" onSubmit={handleSubmit} noValidate>
              <div>
                <label htmlFor="name" className="block mb-1 font-medium">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                  placeholder="Full name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  style={{
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text-color)',
                    borderColor: 'var(--muted-color)',
                  }}
                />
              </div>
  
              <div>
                <label htmlFor="email" className="block mb-1 font-medium">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] opacity-50 cursor-not-allowed"
                  placeholder="Email address"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  disabled
                  style={{
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text-color)',
                    borderColor: 'var(--muted-color)',
                  }}
                />
              </div>
  
              <div>
                <label htmlFor="subject" className="block mb-1 font-medium">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  id="subject"
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                  placeholder="Subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  style={{
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text-color)',
                    borderColor: 'var(--muted-color)',
                  }}
                />
              </div>
  
              <div>
                <label htmlFor="message" className="block mb-1 font-medium">
                  Tell us about the issue
                </label>
                <textarea
                  id="message"
                  name="message"
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                  placeholder="Leave a comment here"
                  required
                  style={{
                    height: '100px',
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text-color)',
                    borderColor: 'var(--muted-color)',
                  }}
                  value={formData.message}
                  onChange={handleChange}
                ></textarea>
              </div>
  
              <div>
                <label htmlFor="priority" className="block mb-1 font-medium">
                  Priority
                </label>
                <select
                  name="priority"
                  id="priority"
                  className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md
                         bg-[var(--input-bg)] text-[var(--text-color)]
                         focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                         focus:border-transparent transition-all"
                  value={formData.priority}
                  onChange={handleChange}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
  
              <div>
                <label className="block mb-1 font-medium">
                  Attachments (optional):
                </label>
                <div
                  onClick={handleAttachmentClick}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <FaPaperclip size={24} className="text-[var(--primary-color)]" />
                  <span className="text-[var(--primary-color)]">Attach Files</span>
                </div>
                <input
                  type="file"
                  name="attachments"
                  id="attachments"
                  multiple
                  onChange={handleChange}
                  ref={attachmentInputRef}
                  className="hidden"
                />
                {formData.attachments && formData.attachments.length > 0 && (
                  <div className="mt-2">
                    {formData.attachments.map((file, index) => (
                      <div key={index} className="text-sm text-[var(--text-color)]">
                        {file.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
  
              <button
                type="submit"
                className="w-full py-2 px-4 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded transition-colors"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Ticket'}
              </button>
              <button
                type="button"
                className="w-full py-2 px-4 bg-[var(--hover-color)] hover:bg-[var(--primary-color)] text-white rounded transition-colors mt-2"
                onClick={() => setView('list')}
              >
                Cancel
              </button>
            </form>
          </div>
        )}
  
        {view === 'chat' && currentTicket && (
          <div className="space-y-6">
            <button
              className="px-4 py-2 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded transition-colors mb-3"
              onClick={goBack}
            >
              Back to Tickets
            </button>
            <h3 className="text-xl font-semibold">
              Ticket Number: {currentTicket.ticketNumber || currentTicket.id}
            </h3>
            <p>Status: {currentTicket.status}</p>
            <p>
              <strong>Subject:</strong> {currentTicket.subject}
            </p>
            <p>
              <strong>Initial Message:</strong> {currentTicket.message}
            </p>
  
            {currentTicket.attachment && currentTicket.attachment.length > 0 && (
              <div>
                <h5 className="text-lg font-semibold mb-2">Attachments</h5>
                <div className="flex flex-wrap gap-2">
                  {currentTicket.attachment.map((url, index) => (
                    <div key={index} className="mb-2">
                      <img
                        src={url}
                        alt={`Attachment ${index + 1}`}
                        className="max-w-[100px] cursor-pointer rounded"
                        onClick={() => openImageModal(url)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
  
            <div className="bg-[var(--card-bg)] rounded p-4 shadow mb-3 max-h-96 overflow-y-auto">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`mb-2 flex ${
                    msg.sender === 'admin' ? 'justify-start' : 'justify-end'
                  }`}
                >
                  <div className="p-2 rounded bg-[var(--card-hover-bg)] text-[var(--text-color)]">
                    {msg.imageUrl && (
                      <div className="flex flex-wrap gap-2 mb-1">
                        <img
                          src={msg.imageUrl}
                          alt={msg.imageName || 'Image'}
                          className="max-w-[100px] cursor-pointer rounded"
                          onClick={() => openImageModal(msg.imageUrl)}
                        />
                      </div>
                    )}
                    {msg.messageText && <small>{msg.messageText}</small>}
                    <br />
                    <small className="text-[var(--muted-color)]">
                      {msg.timestamp &&
                        new Date(msg.timestamp.seconds * 1000).toLocaleString()}
                    </small>
                  </div>
                </div>
              ))}
            </div>
  
            <div className="flex items-center space-x-2">
              <input
                type="text"
                className="flex-grow p-2 border rounded focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                style={{
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--text-color)',
                  borderColor: 'var(--muted-color)',
                }}
              />
              <span
                className="p-2 bg-[var(--card-bg)] border rounded cursor-pointer"
                onClick={handleChatAttachmentClick}
              >
                <FaPaperclip className="text-[var(--primary-color)]" />
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={handleChatAttachmentChange}
                ref={chatAttachmentInputRef}
                className="hidden"
              />
              <button
                className="px-4 py-2 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded transition-colors"
                onClick={sendMessage}
              >
                Send
              </button>
            </div>
            {selectedImage && (
              <div className="mt-2 text-sm text-[var(--text-color)]">
                Selected Image: {selectedImage.name}
              </div>
            )}
  
            {modalImageUrl && (
              <div
                className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
                onClick={closeImageModal}
                aria-modal="true"
                role="dialog"
              >
                <div
                  className="bg-[var(--card-bg)] rounded shadow-lg p-4 max-w-[90vw] max-h-[90vh] overflow-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-center items-center">
                    <img
                      src={modalImageUrl}
                      alt="Full-size"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button
                      className="px-4 py-2 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded transition-colors"
                      onClick={closeImageModal}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
    
  );
  
  
}

export default Support;
